import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  ChangeChannelRequest,
  CreateRequestForQuotationRequest,
  CreateRFQFromProjectCommandResponse,
  CreateSupplierRequestsRequest,
  RequestForQuotationOutboxDto,
  RequestForQuotationDto,
  SendRequestForQuotationResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

export class RequestForQuotationOutboxClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(
      `/api/parties/${partyId}/request-for-quotations`,
      onRequestChange,
    )
  }

  list(): Promise<RequestForQuotationOutboxDto[]> {
    return this.GetAsync({ relativePath: '/outbox', id: 'ListRfqOutbox' })
  }

  detail(rfqId: string): Promise<RequestForQuotationOutboxDto> {
    return this.GetAsync({
      relativePath: `/outbox/${rfqId}`,
      id: `GetRfqOutbox-${rfqId}`,
    })
  }

  send(rfqId: string): Promise<SendRequestForQuotationResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: `/outbox/${rfqId}/send`,
      id: `SendRfqOutbox-${rfqId}`,
    })
  }

  retry(rfqId: string): Promise<SendRequestForQuotationResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: `/outbox/${rfqId}/retry`,
      id: `RetryRfqOutbox-${rfqId}`,
    })
  }

  changeChannel(
    rfqId: string,
    request: ChangeChannelRequest,
  ): Promise<RequestForQuotationOutboxDto> {
    return this.PutAuthenticatedAsync({
      relativePath: `/outbox/${rfqId}/channel`,
      data: request,
      id: `ChangeRfqOutboxChannel-${rfqId}`,
    })
  }

  createSupplierRequests(
    rfqId: string,
    request: CreateSupplierRequestsRequest,
  ): Promise<RequestForQuotationOutboxDto[]> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${rfqId}/supplier-requests`,
      data: request,
      id: `CreateRfqSupplierRequests-${rfqId}`,
    })
  }

  listCanonical(buyerProjectId?: string): Promise<RequestForQuotationDto[]> {
    return this.GetAsync({
      relativePath: '',
      params: buyerProjectId ? { buyerProjectId } : undefined,
      id: 'ListRequestForQuotations',
    })
  }

  createCanonical(
    request: CreateRequestForQuotationRequest,
  ): Promise<CreateRFQFromProjectCommandResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: '',
      data: request,
      id: 'CreateRequestForQuotation',
    })
  }

  deleteCanonical(rfqId: string): Promise<void> {
    return this.DeleteAuthenticatedAsync({
      relativePath: `/${rfqId}`,
      id: `DeleteRequestForQuotation-${rfqId}`,
    })
  }

  download(rfqId: string) {
    return this.DownloadAsync({
      relativePath: `/outbox/${rfqId}/document`,
      id: `DownloadRfqOutbox-${rfqId}`,
    })
  }

  delete(rfqId: string): Promise<void> {
    return this.DeleteAuthenticatedAsync({
      relativePath: `/outbox/${rfqId}`,
      id: `DeleteRfqOutbox-${rfqId}`,
    })
  }
}

export class RequestForQuotationOutboxController extends BaseController<RequestForQuotationOutboxClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new RequestForQuotationOutboxClient(partyId, onRequestChange)
    })
  }

  private handle<T>(operation: () => Promise<T>): Promise<T> {
    return operation().catch((error) => Promise.reject(this.HandleError(error)))
  }

  list() {
    return this.handle(() => this.api.list())
  }

  detail(rfqId: string) {
    return this.handle(() => this.api.detail(rfqId))
  }

  send(rfqId: string) {
    return this.handle(async () => {
      const response = await this.api.send(rfqId)
      if (!response.success) {
        throw new Error(response.message || 'Failed to send request for quotation.')
      }
      return response
    })
  }

  retry(rfqId: string) {
    return this.handle(async () => {
      const response = await this.api.retry(rfqId)
      if (!response.success) {
        throw new Error(response.message || 'Failed to retry request for quotation.')
      }
      return response
    })
  }

  changeChannel(rfqId: string, request: ChangeChannelRequest) {
    return this.handle(() => this.api.changeChannel(rfqId, request))
  }

  createSupplierRequests(
    rfqId: string,
    request: CreateSupplierRequestsRequest,
  ) {
    return this.handle(() => this.api.createSupplierRequests(rfqId, request))
  }

  listCanonical(buyerProjectId?: string) {
    return this.handle(() => this.api.listCanonical(buyerProjectId))
  }

  createCanonical(request: CreateRequestForQuotationRequest) {
    return this.handle(() => this.api.createCanonical(request))
  }

  deleteCanonical(rfqId: string) {
    return this.handle(() => this.api.deleteCanonical(rfqId))
  }

  sendDelivery(deliveryId: string) {
    return this.send(deliveryId)
  }

  async retryDelivery(deliveryId: string) {
    const response = await this.retry(deliveryId)
    if (response.success === false) {
      throw new Error(response.message || 'RFQ retry failed.')
    }
    return response
  }

  downloadDelivery(deliveryId: string) {
    return this.download(deliveryId)
  }

  changeDeliveryChannel(deliveryId: string, request: ChangeChannelRequest) {
    return this.changeChannel(deliveryId, request)
  }

  download(rfqId: string) {
    return this.handle(() => this.api.download(rfqId))
  }

  delete(rfqId: string) {
    return this.handle(() => this.api.delete(rfqId))
  }
}
